import { useRecoilValue } from "recoil";
import { CommandType, SelectedParametersType, UserType, VehicleListType } from "../../../types/types";
import { userAtom } from "../../../atoms/atoms";
import { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { useCookies } from "react-cookie";
import { useLocation } from "react-router-dom";
import { setToastRefToOpen } from "../../../functions/functions";
import findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse from "../../../resources/Api/vehicle/findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse";
import { Autocomplete, Box, Button, Card, CardActions, CardContent, CardHeader, Dialog, DialogActions, DialogContent, Divider, FormControl, Grid, Grow, IconButton, InputLabel, Paper, Stack, styled, Switch, SwitchProps, TextField, Toolbar, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { Abc, CameraFrontOutlined, CameraOutdoorOutlined, CameraOutlined, DisplaySettingsOutlined, MicExternalOnOutlined, MicOutlined, OnlinePredictionOutlined, SearchOutlined, SendOutlined, VideocamOutlined, CloseOutlined } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider, MobileDateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { findAllCommandsByDeviceIdListResponse } from "../../../resources/Api/device/findAllCommandsByDeviceIdListResponse";
import sendCommandById from "../../../resources/Api/device/sendCommandById";
import FlespiVideoPlayer from "./components/ModalPlayer";
import CameraContainer from "./components/CameraConatiner";
import CameraSkeleton from "./components/CameraSkeleton";
import { CAMERA_COMMANDS } from "../../../consts/consts";
import FlespiPhotoViewer from "./components/FlespiFhotoPlayer";
import FlespiVideoTimelineList from "./components/FlespiVideoTimelineList";

type Vehicle = {
    id: number;
    name: string;
    vehiclePlate: string;
    deviceImei?: string;
};

type DeviceCommand = {
    type: string;
    description: string;
};

const getGridSize = (total: number, index: number) => {
    if (total === 1) return 12;
    if (total === 2) return 6;
    if (total === 3) {
        return index === 2 ? 12 : 6;
    }
    return 6; // 4 ou mais
};

const MediaBoxLoader = () => (
    <Box
        sx={{
            height: 650,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        }}
    >
        <div className="tracker-loading-main">
            <div className="ld-tracker ld-tracker-main">
                <div></div>
                <div></div>
                <div></div>
            </div>
        </div>
    </Box>
);

const IOSSwitch = styled((props: SwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#65C466',
                opacity: 1,
                border: 0,
                ...theme.applyStyles('dark', {
                    backgroundColor: '#2ECA45',
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
            ...theme.applyStyles('dark', {
                color: theme.palette.grey[600],
            }),
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
            ...theme.applyStyles('dark', {
                opacity: 0.3,
            }),
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: '#E9E9EA',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
        ...theme.applyStyles('dark', {
            backgroundColor: '#39393D',
        }),
    },
}));

const CameraRow = ({
    label,
    checked,
    audio,
    onToggle,
    onToggleAudio,
    onToggleLabel
}: {
    label: string;
    checked: boolean;
    audio: boolean;
    onToggle: (v: boolean) => void;
    onToggleAudio: (v: boolean) => void;
    onToggleLabel: (v: string) => void;
}) => (
    <Stack
        className="tw-grid tw-grid-cols-1 tw-gap-3 tw-rounded-xl tw-border-0 tw-bg-gtr-surface-muted tw-p-3 tw-transition-colors tw-duration-200 hover:tw-bg-gtr-surface-raised sm:tw-grid-cols-[auto_auto_minmax(0,1fr)]"
        sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "auto auto minmax(0, 1fr)" },
            alignItems: "center",
            gap: 1.25,
            padding: 1.25,
            borderRadius: 3,
            border: "1px solid rgb(var(--gtr-border) / 0.60)",
        }}
    >
            <Stack direction="row" spacing={0.75} alignItems="center">
                <VideocamOutlined fontSize="medium" />
                <IOSSwitch checked={checked} onChange={(e) => onToggle(e.target.checked)} />
                <Typography sx={{ fontFamily: "Prometo", fontSize: 13 }}>Vídeo</Typography>
            </Stack>

            <Stack direction="row" spacing={0.75} alignItems="center">
                <MicOutlined fontSize="medium" />
                <IOSSwitch checked={audio} onChange={(e) => onToggleAudio(e.target.checked)} />
                <Typography sx={{ fontFamily: "Prometo", fontSize: 13 }}>Áudio</Typography>
            </Stack>

            <Stack direction="row" sx={{ width: "100%", minWidth: 0 }} spacing={0.75} alignItems="center">
                <Abc fontSize="medium" />
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Identificação da câmera"
                    value={label}
                    onChange={(e) => onToggleLabel(e.target.value)}
                />
            </Stack>
    </Stack>
);

type CameraStatus = "idle" | "connecting" | "streaming" | "timeout";

type CameraState = {
    id: number;
    label: string;
    enabled: boolean;
    audio: boolean;
    response: string | null;
    status: CameraStatus;
    loadingSince: number;
    closed: boolean;
};

/**
 * VideoTelemetry - Render a component to oepn video telemetry of customer
 * @component
 */
const VideoTelemetry = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    const isMobile = useMediaQuery("(max-width:650px)");
    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // functionalities states
    const [isOnline, setIsOnline] = useState(true);
    const [loadingMediaBox, setLoadingMediaBox] = useState(true);
    const [loading, setLoading] = useState(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<"warn" | "success" | "info" | "error" | undefined>("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [inEdit, setInEdit] = useState<boolean>(false);
    const [inInsert, setInInsert] = useState<boolean>(false);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [openMediabox, setOpenMediaBox] = useState<boolean>(false);
    const [query, setQuery] = useState<string>("");

    // states
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

    const [report, setReport] = useState<ArrayBuffer>(new ArrayBuffer(0));
    const [exportType, setExportType] = useState<string>("");
    const [reportType, setReportType] = useState<string>("common");

    const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
    const [isOpenDialogCameraField, setIsOpenDialogCameraField] = useState<boolean>(false);

    const [vehicleList, setVehicleList] = useState<Array<VehicleListType>>([]);
    const [deviceCommandName, setDeviceCommandName] = useState<string>("");
    const [deviceCommandsList, setDeviceCommandList] = useState<Array<CommandType>>([]);
    const [selectedVehicle, setSelectedVehicle] = useState<VehicleListType | null>(null);
    const [commandResult, setCommandResult] = useState<string>("");

    const [cameras, setCameras] = useState<CameraState[]>([
        {
            id: 1,
            label: "Câmera 1",
            enabled: true,
            audio: false,
            response: null,
            status: "idle",
            loadingSince: 0,
            closed: false,
        },
        {
            id: 2,
            label: "Câmera 2",
            enabled: false,
            audio: false,
            response: null,
            status: "idle",
            loadingSince: 0,
            closed: false,
        },
        {
            id: 3,
            label: "Câmera 3",
            enabled: false,
            audio: false,
            response: null,
            status: "idle",
            loadingSince: 0,
            closed: false,
        },
        {
            id: 4,
            label: "Câmera 4",
            enabled: false,
            audio: false,
            response: null,
            status: "idle",
            loadingSince: 0,
            closed: false,
        },
    ]);

    // refs
    const toastModalRef = useRef<Toast>(null);
    const toastRef = useRef<Toast>(null);
    const anchorRef = useRef<HTMLButtonElement | null>(null);
    const autcompleteRef = useRef<any>(null);

    // cookies
    const [cookies, _] = useCookies(["selectedParameters", "token"]);

    // hooks and consts
    const location = useLocation();
    const currentParameters: SelectedParametersType = useMemo(() => cookies.selectedParameters, [cookies.selectedParameters]);
    const camerasWithStream = useMemo(() => cameras.filter((cam) => cam.enabled), [cameras]);

    const handleClose = useCallback(() => {
        setLoading(false);
        setStatusMessage("warn");
        setIsToastVisible(false);
        setTextValidation("");
        setIsOpenDetails(false);
        setStartDate(dayjs());
        setEndDate(dayjs());
        setDeviceCommandName("");
        setSelectedVehicle(null);
        setCameras([
            {
                id: 1,
                label: "Câmera 1",
                enabled: true,
                audio: false,
                response: null,
                status: "idle",
                loadingSince: 0,
                closed: false,
            },
            {
                id: 2,
                label: "Câmera 2",
                enabled: false,
                audio: false,
                response: null,
                status: "idle",
                loadingSince: 0,
                closed: false,
            },
            {
                id: 3,
                label: "Câmera 3",
                enabled: false,
                audio: false,
                response: null,
                status: "idle",
                loadingSince: 0,
                closed: false,
            },
            {
                id: 4,
                label: "Câmera 4",
                enabled: false,
                audio: false,
                response: null,
                status: "idle",
                loadingSince: 0,
                closed: false,
            }]);
    }, []);

    useEffect(() => {
        const ref = isOpenDetails ? toastModalRef : toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [
        isOpenDetails,
        validationText,
        isFieldsValid,
        statusMessage,
        isToastVisible,
    ]);

    const getAllVehicles = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const currentParameters: SelectedParametersType = cookies.selectedParameters;

        const customerId = currentParameters?.customer.id;
        const departmentId = currentParameters?.department.id;

        setLoading(true);
        try {
            const { data } =
                await findAllVehiclesByCustomerIdAndSecretaryIdAndDepartmentIdListResponse(
                    token,
                    customerId,
                    departmentId,
                    currentParameters?.isViewingAll
                );
            setVehicleList(data.filter((vehicle: VehicleListType) => vehicle.deviceCameraId));
        } catch (error: any) {
            console.log(error);
            setVehicleList([]);
        } finally {
            setTimeout(() => {
                setLoading(false);
            }, 2000);
        }
    }, [cookies.selectedParameters, cookies.token, getCurrentUser.configs.isManager]);

    useEffect(() => {
        getAllVehicles();
    }, [getAllVehicles]);

    const getAllCommandsByDevice = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        try {
            const { data } =
                await findAllCommandsByDeviceIdListResponse(
                    token,
                    currentParameters.customer.id,
                    selectedVehicle?.deviceCameraId as any,
                    "CAMERA"
                );
            setDeviceCommandList(data);
        } catch (error: any) {
            console.log(error);
            setDeviceCommandList([]);
            const { data } = error.response;

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        }
    }, [cookies.token, selectedVehicle?.deviceCameraId, currentParameters.customer.id]);

    useEffect(() => {
        if (selectedVehicle?.deviceCameraImei) {
            getAllCommandsByDevice();
        }
    }, [getAllCommandsByDevice, selectedVehicle?.deviceCameraImei]);

    const updateCamera = useCallback((id: number, data: Partial<typeof cameras[0]>) => {
        setCameras((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));
    }, []);

    const sendCommandForCamera = useCallback(async (cam: any) => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const customerId = currentParameters?.customer?.id;
        const isCommandLine = false;

        const comandData: SendCommandType = {
            token: token,
            deviceType: "CAMERA",
            commandDesc: deviceCommandName,
            deviceId: selectedVehicle?.deviceCameraId as number,
            deviceImei: `${selectedVehicle?.deviceCameraImei}`,
            customerId: customerId,
            name: getCurrentUser.name,
            email: getCurrentUser?.email,
            isCommandLine: isCommandLine,
            cameraConfig: {
                audio: cam.audio,
                channel: cam.id,
                type: deviceCommandName,
                duration: null,
                from: startDate ? Math.floor(startDate?.valueOf() / 1000) : null,
                to: endDate ? Math.floor(endDate?.valueOf() / 1000) : null,
                streamType: null,
                mediaStream: null,
                id: cam.id,
            }
        }

        try {
            const { data } = await sendCommandById(comandData);
            setTextValidation("Comando enviado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);

            // console.log(data);
            // setCommandResult(data.responseCamera);
            updateCamera(cam.id, { response: data.responseCamera, status: "streaming" });
        } catch (error: any) {
            console.log(error);
            const { data } = error.response;
            updateCamera(cam.id, { response: null, closed: false, status: "timeout" });
            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setIsOpenDetails(false);
            setTextValidation(data.reason);
        }
    }, [
        startDate,
        endDate,
        selectedVehicle?.deviceId,
        getCurrentUser.configs.isManager,
        cookies.token,
        deviceCommandName,
        getCurrentUser.email,
        currentParameters?.customer.id,
        getCurrentUser.name,
        selectedVehicle?.deviceCameraImei,
        selectedVehicle?.deviceCameraId,
        selectedVehicle?.name,
        selectedVehicle?.vehiclePlate,
        updateCamera
    ]);

    const onSendCameraCommand = useCallback(() => {
        if (camerasWithStream.length === 0 && deviceCommandName !== CAMERA_COMMANDS.DOWNLOAD_EVENT_MEDIA.description) {
            setStatusMessage("warn");
            setFieldsIsValid(false);
            setTextValidation("Selecione ao menos uma câmera.");
            return;
        }

        setIsOpenDialogCameraField(true);
        for (const cam of camerasWithStream) {
            updateCamera(cam.id, { response: null, closed: false, status: "connecting", loadingSince: Date.now() });
            sendCommandForCamera(cam);
        }
    }, [camerasWithStream, deviceCommandName, sendCommandForCamera, updateCamera]);

    useEffect(() => {
        if (camerasWithStream.length > 0 && camerasWithStream.every((item) => item.closed == true)) {
            if (!isToastVisible) {
                setIsOpenDialogCameraField(false);
                handleClose();
            }
        }
    }, [camerasWithStream, isToastVisible, handleClose]);

    const timeoutRefs = useRef<Record<number, NodeJS.Timeout>>({});

    useEffect(() => {
        cameras.forEach((cam) => {
            // só cria timeout se estiver conectando
            if (cam.status === "connecting" && cam.loadingSince) {
                // evita duplicar timeout
                if (timeoutRefs.current[cam.id]) return;

                timeoutRefs.current[cam.id] = setTimeout(() => {
                    setCameras((prev) =>
                        prev.map((c) =>
                            c.id === cam.id && c.status === "connecting"
                                ? { ...c, status: "timeout" }
                                : c
                        )
                    );

                    delete timeoutRefs.current[cam.id];
                }, 60000);
            }

            // se saiu de connecting, limpa timeout
            if (cam.status !== "connecting" && timeoutRefs.current[cam.id]) {
                clearTimeout(timeoutRefs.current[cam.id]);
                delete timeoutRefs.current[cam.id];
            }
        });

        return () => {
            Object.values(timeoutRefs.current).forEach(clearTimeout);
            timeoutRefs.current = {};
        };
    }, [cameras]);

    const deviceCameraFieldDialog = useMemo(() => {
        return (
            <>
                <Dialog
                    open={isOpenDialogCameraField}
                    onClose={handleClose}
                    PaperProps={{
                        className: "tw-overflow-hidden tw-rounded-2xl tw-border-0 tw-bg-gtr-surface tw-text-gtr-text tw-shadow-gtr-modal",
                        sx: {
                            width: "min(94vw, 1280px)",
                            maxWidth: "unset",
                            maxHeight: "calc(100dvh - 24px)",
                            borderRadius: 4,
                            boxShadow: "var(--gtr-shadow-modal)",
                        },
                    }}
                    style={{
                        display: "flex",
                        height: "100%",
                        width: "100%",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        justifyContent: "center",
                        minHeight: "500px",
                        alignItems: "center",
                    }}
                    aria-labelledby=""
                >
                    <Box className="tw-bg-gtr-surface tw-p-3 tw-text-gtr-text sm:tw-p-4" sx={{ padding: { xs: 1.5, sm: 2 } }}>
                        <Grid container spacing={2}>
                            {camerasWithStream.map((cam, index) => {
                                const md = getGridSize(camerasWithStream.length, index);
                                return (
                                    <Grow
                                        in={!cam.closed}
                                        timeout={300}
                                        key={cam.id}
                                        mountOnEnter
                                        unmountOnExit
                                    >
                                        <Grid item xs={12} md={md} sx={{ display: "flex" }}>
                                            <CameraContainer>

                                                {/* LOADING */}
                                                {cam.status === "connecting" && <CameraSkeleton />}

                                                {cam.status !== "connecting" && (deviceCommandName === CAMERA_COMMANDS.START_VIDEOSTREAM.description) && (
                                                    <FlespiVideoPlayer
                                                        label={cam.label}
                                                        status={cam.status}
                                                        data={cam.response}
                                                        onClose={() =>
                                                            updateCamera(cam.id, { response: null, closed: true })
                                                        }
                                                    />
                                                )}

                                                {cam.status !== "connecting" && (deviceCommandName === CAMERA_COMMANDS.TAKE_PHOTO.description) && (
                                                    <FlespiPhotoViewer
                                                        label={cam.label}
                                                        status={cam.status}
                                                        data={cam.response}
                                                        onClose={() =>
                                                            updateCamera(cam.id, { response: null, closed: true })
                                                        }
                                                    />
                                                )}

                                                {cam.status !== "connecting" && (deviceCommandName === CAMERA_COMMANDS.DOWNLOAD_EVENT_MEDIA.description) && (
                                                    <FlespiVideoTimelineList
                                                        label={cam.label}
                                                        status={cam.status}
                                                        data={cam.response}
                                                        startDate={startDate}
                                                        endDate={endDate}
                                                        selectedVehicleId={selectedVehicle?.id}
                                                        onClose={() =>
                                                            updateCamera(cam.id, { response: null, closed: true })
                                                        }
                                                    />
                                                )}
                                            </CameraContainer>
                                        </Grid>
                                    </Grow>
                                )
                            })}
                        </Grid>

                        <DialogActions className="tw-mt-4 tw-border-t tw-border-gtr-border/60 tw-bg-gtr-surface tw-pt-4" sx={{ paddingTop: 2, marginTop: 2 }}>
                            <Box
                                className="tw-flex tw-w-full tw-justify-end"
                                sx={{ display: "flex", width: "100%", justifyContent: "flex-end", paddingTop: 2 }}
                            >
                                <Paper
                                    elevation={0}
                                    style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        background: "transparent",
                                        marginLeft: "auto",
                                    }}
                                >
                                    <Button
                                        type="button"
                                        style={{ fontFamily: "Prometo", color: "#ffff" }}
                                        disabled={false}
                                        onClick={() => {
                                            if (!isToastVisible) {
                                                setIsOpenDialogCameraField(false);
                                            }
                                        }}
                                        className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                                    >
                                        <CloseOutlined style={{ marginRight: 5 }} />
                                        <Typography sx={{ color: "#fff", fontFamily: "Prometo", fontWeight: 700 }}>
                                            Fechar
                                        </Typography>
                                    </Button>
                                </Paper>
                            </Box>
                        </DialogActions>
                    </Box>
                </Dialog>
            </>
        )
    }, [selectedVehicle?.id, deviceCommandName, isOpenDialogCameraField, updateCamera, camerasWithStream, commandResult, getCurrentUser.configs.isManager, handleClose, inEdit, inInsert, isToastVisible, selectedVehicle?.name, startDate, endDate]);

    return (
        <Box>
            <Toast
                ref={toastRef}
                position="center"
                style={{ position: "absolute" }}
                onShow={() => {
                    setTimeout(() => {
                        setTextValidation("");
                        setStatusMessage("warn");
                        setIsToastVisible(false);
                    }, 3500);
                }}
            />
            {deviceCameraFieldDialog}
            <Box
                className="video-telemetry-root grid-box-out-container tw-flex tw-min-h-screen tw-w-full tw-items-center tw-justify-center tw-bg-transparent tw-p-0.5"
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    minHeight: "100vh",
                    background: "transparent",
                }}
            >
                <Box
                    className="grid-container container container-fluid tw-w-full tw-overflow-hidden tw-rounded-xl tw-border-0 tw-bg-gtr-surface tw-p-2.5 tw-text-gtr-text tw-shadow-none"
                    style={{
                        marginTop: 0,
                        transition: "all 0.3s ease-out allow-discrete",
                        width: isMobile ? "100%" : (isHiddenMenu ? "70vw" : "65vw"),
                        maxHeight: "calc(100dvh - 32px)",
                        overflowY: "auto",
                        borderRadius: 12,
                        position: "relative"
                    }}
                >

                    <Toolbar
                        className="grid-header-bar tw-rounded-xl tw-bg-gtr-sidebar tw-px-3 tw-text-white tw-shadow-sm sm:tw-px-4"
                        style={{
                            backgroundImage: "linear-gradient(to right, #09414A , #09414A)",
                            fontFamily: "Prometo",
                            marginTop: 10,
                            marginBottom: 10,
                            borderRadius: 12,
                            color: "#ffff",
                            position: "relative",
                            minHeight: 56,
                        }}
                    >
                        <Box
                            className="header-actions tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3"
                            sx={{
                                display: "flex",
                                width: "100%",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1.5,
                                fontFamily: "Prometo",
                            }}
                        >
                            <Box className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                                <Box className="tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/10">
                                    <VideocamOutlined />
                                </Box>
                                <Typography className="tw-truncate" sx={{ fontFamily: "Prometo", fontWeight: 700, fontSize: { xs: 16, sm: 18 } }}>
                                    Vídeo Telemetria
                                </Typography>
                            </Box>
                            <Box className="tw-flex tw-flex-none tw-items-center tw-gap-2">
                                {openMediabox && (
                                    <Box className="tw-hidden tw-items-center tw-gap-2 tw-rounded-lg tw-bg-white/10 tw-px-3 tw-py-2 sm:tw-flex">
                                        <IOSSwitch checked={openMediabox} onChange={(e) => {
                                            setOpenMediaBox(e.target.checked);
                                            setLoadingMediaBox(true);

                                            setTimeout(() => {
                                                setLoadingMediaBox(false);
                                            }, 1000);
                                        }} />
                                        <Typography sx={{ fontFamily: "Prometo", fontSize: 13 }}>Modo avançado</Typography>
                                    </Box>
                                )}
                                <Tooltip title="Fechar" arrow>
                                    <IconButton
                                        aria-label="Fechar vídeo telemetria"
                                        onClick={() => {
                                            window.location.replace("/")
                                        }}
                                        sx={{ color: "#ffff", transition: "background-color 0.2s ease-out, transform 0.2s ease-out", "&:hover": { backgroundColor: "rgba(255,255,255,0.12)", transform: "translateY(-1px)" } }}
                                    >
                                        <CloseOutlined />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Toolbar>

                    {!openMediabox ? <Grid container spacing={2} className="tw-rounded-xl tw-bg-gtr-surface tw-p-1">
                        <Grid item xs={12} md={6}>
                            <Card className="tw-h-full tw-overflow-hidden tw-rounded-xl tw-border-0 tw-bg-gtr-surface tw-text-gtr-text tw-shadow-none" elevation={0} sx={{ borderRadius: 3, border: "none", boxShadow: "none", height: "100%" }}>
                                <CardHeader
                                    className="tw-border-b tw-border-gtr-border/60 tw-bg-gtr-surface-muted"
                                    avatar={<Box className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-gtr-teal/10 tw-text-gtr-teal"><DisplaySettingsOutlined /></Box>}
                                    title="Configuração do comando"
                                    subheader="Selecione o veículo e o comando"
                                    sx={{ py: 1.5, "& .MuiCardHeader-title": { fontFamily: "Prometo", fontSize: 17, fontWeight: 700 }, "& .MuiCardHeader-subheader": { fontFamily: "Prometo", fontSize: 13 } }}
                                />
                                <CardContent
                                    sx={{
                                        pt: 1,
                                        "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "rgb(var(--gtr-surface-panel))" },
                                        "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
                                        "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
                                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700" },
                                    }}
                                >
                                    <Stack spacing={2}>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ width: "100%" }}
                                        >
                                            <Autocomplete
                                                id="chip-label-select-vehicle"
                                                className="input-select-vehicle"
                                                disabled={false}
                                                style={{
                                                    width: "100%",
                                                    minHeight: "56px",
                                                    height: "56px",
                                                    backgroundColor: "rgb(var(--gtr-surface-panel) / 0.16)",
                                                    borderColor: "rgb(var(--gtr-border))",
                                                    fontFamily: "Prometo",
                                                }}
                                                groupBy={(option) => option.vehicleTypeName}
                                                options={vehicleList.sort(
                                                    (a, b) => -b?.vehicleTypeName.localeCompare(a?.vehicleTypeName)
                                                )}
                                                value={selectedVehicle}
                                                onChange={(_, value) => {
                                                    setSelectedVehicle(value ?? null);
                                                }}
                                                getOptionLabel={(option) =>
                                                    `${option?.name} - ${option?.vehiclePlate}(${option?.deviceImei ?? option?.satelliteImei ?? option?.deviceCameraImei ?? "Sem dispositivo"})`
                                                }
                                                noOptionsText={"Nada encontrado"}
                                                renderInput={(params) => (
                                                    <>
                                                        <FormControl
                                                            className="form-group"
                                                            style={{ display: "flex" }}
                                                        >
                                                            <InputLabel
                                                                id="vehicles-label"
                                                                required={true}
                                                                style={{
                                                                    color: "rgba(0, 0, 0, 0.6)",
                                                                    fontFamily: "Prometo",
                                                                }}
                                                                shrink
                                                            >
                                                                Veículo
                                                            </InputLabel>
                                                            <TextField
                                                                {...params}
                                                                variant="outlined"
                                                                placeholder={"Selecione um veículo"}
                                                                required={true}
                                                                InputLabelProps={{ shrink: true }}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    notched: true,
                                                                    label: "Veículo",
                                                                    classes: {
                                                                        notchedOutline: "custom-notch",
                                                                    },
                                                                }}
                                                            />
                                                        </FormControl>
                                                    </>
                                                )}
                                            />
                                        </Stack>

                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ width: "100%" }}
                                        >
                                            <Autocomplete
                                                id="chip-label-select-command"
                                                className="input-select-command"
                                                style={{
                                                    width: "100%",
                                                    backgroundColor: "rgb(var(--gtr-surface-panel) / 0.16)",
                                                    borderColor: "rgb(var(--gtr-border))",
                                                }}
                                                onChange={(_, value) => {
                                                    setDeviceCommandName(value?.description ?? "");
                                                }}
                                                options={deviceCommandsList}
                                                disabled={openMediabox}
                                                value={deviceCommandsList.find((a) => a.description === deviceCommandName) ?? null}
                                                getOptionLabel={(option) => `${option.description} - (${option.type})`}
                                                noOptionsText={"Nada encontrado"}
                                                renderInput={(params) => (
                                                    <>
                                                        <FormControl
                                                            className="form-group"
                                                            style={{ display: "flex" }}
                                                            disabled={openMediabox}
                                                        >
                                                            <InputLabel
                                                                id="vehicles-label"
                                                                required={false}
                                                                style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}
                                                                shrink
                                                            >
                                                                Comando
                                                            </InputLabel>
                                                            <TextField
                                                                {...params}
                                                                variant="outlined"
                                                                disabled={openMediabox}
                                                                placeholder="Selecione uma comando"
                                                                autoComplete="off"
                                                                required={false}
                                                                InputLabelProps={{ shrink: true }}
                                                                InputProps={{
                                                                    ...params.InputProps,
                                                                    notched: true,
                                                                    label: "Comando",
                                                                    classes: {
                                                                        notchedOutline: "custom-notch",
                                                                    },
                                                                }}
                                                            />
                                                        </FormControl>
                                                    </>
                                                )}
                                            />
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{
                                                p: 1.25,
                                                borderRadius: 2,
                                                border: "1px solid rgba(0,63,74,0.10)",
                                                bgcolor: "rgba(0,108,119,0.025)",
                                            }}
                                        >
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <DisplaySettingsOutlined fontSize="medium" />
                                                <IOSSwitch checked={openMediabox} onChange={(e) => {
                                                    if (!selectedVehicle) {
                                                        setStatusMessage("warn");
                                                        setFieldsIsValid(false);
                                                        setTextValidation("Selecione um veículo.");
                                                        return;
                                                    }

                                                    setOpenMediaBox(e.target.checked);
                                                    setLoadingMediaBox(true);

                                                    setTimeout(() => {
                                                        setLoadingMediaBox(false);
                                                    }, 1000);
                                                }} />
                                                <Typography style={{ marginLeft: "5px" }}>Modo avançado</Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Card className="tw-h-full tw-overflow-hidden tw-rounded-xl tw-border-0 tw-bg-gtr-surface tw-text-gtr-text tw-shadow-none" elevation={0} sx={{ borderRadius: 3, border: "none", boxShadow: "none", height: "100%" }}>
                                <CardHeader
                                    className="tw-border-b tw-border-gtr-border/60 tw-bg-gtr-surface-muted"
                                    avatar={<Box className="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-gtr-green/10 tw-text-gtr-green"><CameraOutlined /></Box>}
                                    title={deviceCommandName === CAMERA_COMMANDS.DOWNLOAD_EVENT_MEDIA.description ? "Período da consulta" : "Câmeras"}
                                    subheader={deviceCommandName === CAMERA_COMMANDS.DOWNLOAD_EVENT_MEDIA.description ? "Selecione um período" : "Selecione as câmeras"}
                                    sx={{ py: 1.5, "& .MuiCardHeader-title": { fontFamily: "Prometo", fontSize: 17, fontWeight: 700 }, "& .MuiCardHeader-subheader": { fontFamily: "Prometo", fontSize: 13 } }}
                                />
                                <CardContent
                                    sx={{
                                        pt: 1,
                                        "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "rgb(var(--gtr-surface-panel))" },
                                        "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
                                        "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
                                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700" },
                                    }}
                                >
                                    {deviceCommandName !== CAMERA_COMMANDS.DOWNLOAD_EVENT_MEDIA.description ? <Stack spacing={1}>
                                        {cameras.map((cam) => (
                                            <CameraRow
                                                key={cam.id}
                                                label={cam.label}
                                                checked={cam.enabled}
                                                audio={cam.audio}
                                                onToggle={(v) => updateCamera(cam.id, { enabled: v })}
                                                onToggleAudio={(v) => updateCamera(cam.id, { audio: v })}
                                                onToggleLabel={(v) => updateCamera(cam.id, { label: v })}
                                            />
                                        ))}
                                    </Stack> : <Stack spacing={3}>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ width: "100%" }}>
                                            <FormControl
                                                className="form-group"
                                                style={{
                                                    width: "100%"
                                                }}
                                            >
                                                <LocalizationProvider
                                                    dateAdapter={AdapterDayjs}
                                                    adapterLocale={"pt-br"}
                                                >
                                                    <MobileDateTimePicker
                                                        sx={{ width: "100%" }}
                                                        label={"Data início"}
                                                        format="DD/MM/YYYY HH:mm:ss"
                                                        onChange={(value) => {
                                                            setStartDate(value?.isValid() ? value : null);
                                                        }}
                                                        value={startDate?.startOf("day")}
                                                        minDate={dayjs().subtract(30, "days").startOf("day")}
                                                        maxDate={dayjs()}
                                                    />
                                                </LocalizationProvider>
                                            </FormControl>
                                        </Stack>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ width: "100%" }}>
                                            <FormControl
                                                className="form-group"
                                                style={{
                                                    width: "100%"
                                                }}
                                            >
                                                <LocalizationProvider
                                                    dateAdapter={AdapterDayjs}
                                                    adapterLocale={"pt-br"}
                                                >
                                                    <MobileDateTimePicker
                                                        sx={{ width: "100%" }}
                                                        label={"Data final"}
                                                        format="DD/MM/YYYY HH:mm:ss"
                                                        onChange={(value) => {
                                                            setEndDate(value?.isValid() ? value : null);
                                                        }}
                                                        value={endDate?.endOf("day")}
                                                        minDate={dayjs(startDate?.startOf("day")).add(1, "hour")}
                                                        maxDate={dayjs()}
                                                    // disableFuture
                                                    />
                                                </LocalizationProvider>
                                            </FormControl>
                                        </Stack>
                                    </Stack>}
                                </CardContent>
                                <CardActions className="tw-border-t tw-border-gtr-border/60 tw-bg-gtr-surface" sx={{ justifyContent: "flex-end", p: 2 }}>
                                    <Button
                                        className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                                        size="large"
                                        onClick={() => {
                                            onSendCameraCommand();
                                        }}
                                        disabled={!selectedVehicle || !deviceCommandName}
                                    >
                                        <SendOutlined sx={{ color: "#fff", marginRight: 1 }} />
                                        <Typography
                                            className="forgot-password"
                                            style={{ fontFamily: "Prometo", fontSize: "16px", color: "#ffff" }}
                                        >
                                            Enviar
                                        </Typography>
                                    </Button>
                                </CardActions>
                            </Card>

                        </Grid>
                    </Grid> : <Grid container className="tw-rounded-xl tw-bg-gtr-surface tw-p-1">
                        <Card className="tw-w-full tw-overflow-hidden tw-rounded-xl tw-border-0 tw-bg-gtr-surface tw-text-gtr-text tw-shadow-none" elevation={0} sx={{ width: "100%", borderRadius: 3, border: "none", boxShadow: "none" }}>
                            <CardContent
                                sx={{
                                    p: 0,
                                        height: "min(70vh, 680px)",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        height: "100%",
                                    }}
                                >
                                    {/* LOADING */}
                                    {loadingMediaBox && (
                                        <Box
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                zIndex: 2,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                bgcolor: "rgba(0,0,0,0.6)",
                                            }}
                                        >
                                            <MediaBoxLoader />
                                        </Box>
                                    )}

                                    {/* IFRAME */}
                                    <iframe
                                        src={`https://mediabox.flespi.io/#/device/${selectedVehicle?.deviceCameraImei}?token=LlIfeCrk3rySG1KuHcxKp8GD7Oex7WIJjvPnRc2s061NG5dOOyoDTFjPQ04lflnU`}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            border: "none",
                                        }}
                                        allow="autoplay; fullscreen"
                                        onLoad={() => setLoadingMediaBox(false)}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    }
                </Box>
            </Box>
        </Box>
    );
};

const style: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: "#f3f4f6",
    border: "none",
    boxShadow: "none",
    borderRadius: 1,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 3,
    width: "740px",
    fontFamily: "Prometo",
};

type SendCommandType = {
    token: string;
    deviceType: string;
    commandDesc: string;
    deviceId: number;
    deviceImei: string;
    customerId: number;
    name: string;
    email: string;
    isCommandLine: boolean;
    cameraConfig: {
        id: number;
        type: string;
        channel: number | null;
        audio: boolean;
        mediaStream: string | null;
        streamType: string | null;
        from: number | null;
        to: number | null;
        duration: number | null;
    } | null;
};

export default VideoTelemetry;
